<template>
  <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <div v-if="!compact" class="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
      <div>
        <h2 class="text-sm font-semibold text-gray-900">Data Feed Registry</h2>
        <p class="text-xs text-gray-500">Status of each tenant data source at a glance</p>
      </div>
      <button
        class="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:border-gray-400 disabled:opacity-50"
        :disabled="loading"
        @click="refresh"
      >
        Refresh
      </button>
    </div>

    <div :class="compact ? 'p-3' : 'p-4'">
      <LoadingIndicator v-if="loading && !summary" message="Loading feed registry..." />

      <div v-if="error" class="text-xs text-red-600 mb-3">
        {{ error }}
        <button class="underline ml-1" @click="refresh">Retry</button>
      </div>

      <template v-if="summary">
        <!-- Overall + meta line -->
        <div class="flex items-center gap-2 mb-3 text-xs text-gray-500">
          <span class="h-2 w-2 rounded-full" :class="statusDotClass(summary.overall_status)"></span>
          <span class="font-medium text-gray-700">{{ summary.feed_count }} feeds</span>
          <span class="text-gray-300">·</span>
          <span>{{ (summary.domains || []).length }} groups</span>
          <span v-if="lastFetched && !compact" class="ml-auto text-gray-400">{{ lastFetchedDisplay }}</span>
        </div>

        <!-- Domain groups: dense, collapsible -->
        <div class="border border-gray-100 rounded divide-y divide-gray-100">
          <div v-for="group in summary.domains" :key="group.domain">
            <button
              type="button"
              class="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 text-left"
              @click="toggleGroup(group.domain)"
            >
              <span class="h-2 w-2 rounded-full shrink-0" :class="statusDotClass(group.status)"></span>
              <span class="text-sm font-medium text-gray-800">{{ group.domain_label }}</span>
              <span class="text-xs text-gray-400">{{ group.feed_count }}</span>
              <span class="ml-auto text-xs text-gray-400 font-mono">{{ countsSummary(group) }}</span>
              <font-awesome-icon
                :icon="['fas', isGroupOpen(group.domain) ? 'chevron-down' : 'chevron-right']"
                class="text-gray-300 text-xs"
              />
            </button>

            <div v-if="isGroupOpen(group.domain)" class="bg-gray-50/50">
              <div
                v-for="feed in group.feeds"
                :key="feed.feed_id"
                class="px-3 py-1.5 pl-8 border-t border-gray-100 first:border-t-0"
              >
                <div class="flex items-center gap-2 text-sm">
                  <span class="h-1.5 w-1.5 rounded-full shrink-0" :class="statusDotClass(feed.status)"></span>
                  <span class="text-gray-800 whitespace-nowrap text-xs">{{ feed.name }}</span>
                  <span
                    v-if="feed.last_filename"
                    class="text-xs text-gray-400 font-mono truncate"
                    :title="feed.last_filename"
                  >
                    {{ feed.last_filename }}
                  </span>
                  <span class="ml-auto text-xs text-gray-500 whitespace-nowrap">
                    <template v-if="feed.last_success_at">
                      {{ formatDate(feed.last_success_at) }}
                      <template v-if="feed.last_rows_loaded != null"> · {{ formatRows(feed.last_rows_loaded) }}</template>
                      <template v-if="feed.age_hours != null"> · {{ formatAgeHours(feed.age_hours) }}</template>
                    </template>
                    <template v-else>no successful run</template>
                  </span>
                  <button
                    v-if="!compact && (feed.schema_id || feed.routine_id)"
                    type="button"
                    class="text-xs text-primary-600 hover:text-primary-800 hover:underline whitespace-nowrap"
                    :title="feed.routine_id
                      ? 'Open ingestion routine'
                      : `Open ingestion schema ${feed.schema_name || feed.schema_id}`"
                    @click="openIngestion(feed)"
                  >
                    {{ feed.routine_id ? 'routine' : 'schema' }}
                    <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" class="text-[9px]" />
                  </button>
                  <button
                    v-if="!compact"
                    type="button"
                    class="text-xs text-gray-400 hover:text-gray-700 whitespace-nowrap"
                    @click="toggleFiles(feed)"
                  >
                    files
                  </button>
                </div>

                <!-- Recent files (lazy) -->
                <div v-if="filesState(feed).open" class="mt-1 ml-4">
                  <LoadingIndicator v-if="filesState(feed).loading" message="Loading..." />
                  <div v-else-if="filesState(feed).error" class="text-xs text-red-600">
                    {{ filesState(feed).error }}
                  </div>
                  <div v-else-if="!filesState(feed).files.length" class="text-xs text-gray-400 italic">
                    No file history (direct Snowflake load)
                  </div>
                  <table v-else class="w-full text-xs">
                    <tbody>
                      <tr v-for="(f, i) in filesState(feed).files" :key="i" class="text-gray-500">
                        <td class="py-0.5 pr-3 font-mono text-gray-700 break-all">{{ f.filename }}</td>
                        <td class="py-0.5 pr-3 whitespace-nowrap">{{ formatDate(f.loaded_at) }}</td>
                        <td class="py-0.5 text-right whitespace-nowrap">{{ f.rows != null ? formatRows(f.rows) : '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { formatDate, statusDotClass } from '@/utils/etlFormat';
import { dataIngestionLocation } from '../../../router/data_routes';

// Worst-first rank, for computing the overall tenant dot from the domains.
const STATUS_RANK = { never_succeeded: 3, stale: 2, overdue: 1, on_schedule: 0 };

/**
 * The feed-registry dashboard. Renders in two modes:
 * - full (the /data Feeds tab): refresh header + per-feed file drill-down
 *   against the superuser ETL endpoints.
 * - compact (the Data Completeness insight tile, via FeedRegistryPanel):
 *   auth-open summary endpoint, no drill-down (its files endpoint is
 *   superuser-only).
 * One component, one formatter set — the old copy-paste sibling showed the
 * same feed with a different date on the two pages.
 */
export default {
  name: 'OverviewEtlFeedRegistry',
  components: { LoadingIndicator },
  props: {
    compact: { type: Boolean, default: false },
    endpoint: { type: String, default: '/api/etl_module/tenant-feeds/summary/' },
  },
  data() {
    return {
      summary: null,
      loading: false,
      error: null,
      lastFetched: null,
      filesByFeed: {}, // keyed by feed_id: { open, loading, error, files, loaded }
      openGroups: {}, // keyed by domain: bool
    };
  },
  computed: {
    lastFetchedDisplay() {
      if (!this.lastFetched) return '';
      const diffSec = Math.floor((Date.now() - this.lastFetched.getTime()) / 1000);
      if (diffSec < 60) return 'updated just now';
      if (diffSec < 3600) return `updated ${Math.floor(diffSec / 60)}m ago`;
      return `updated ${this.lastFetched.toLocaleTimeString()}`;
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    formatDate,
    statusDotClass,
    async refresh() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(this.endpoint);
        const data = response.data;
        // Derive the overall tenant status from the worst domain.
        data.overall_status = (data.domains || []).reduce(
          (worst, g) => (STATUS_RANK[g.status] > STATUS_RANK[worst] ? g.status : worst),
          'on_schedule',
        );
        this.summary = data;
        this.lastFetched = new Date();
        // Default: open any group that needs attention, collapse healthy ones.
        // Preserve the user's own open/closed choices (and loaded file
        // drill-downs) across refreshes.
        const open = { ...this.openGroups };
        (data.domains || []).forEach((g) => {
          if (!(g.domain in open)) open[g.domain] = g.status !== 'on_schedule';
        });
        this.openGroups = open;
      } catch (err) {
        this.error = err.response?.data?.detail || err.message || 'Failed to load feed registry.';
      } finally {
        this.loading = false;
      }
    },
    // Jump to this feed's ingestion routine or schema in the Ingestion tab.
    // The URL carries the target (prefer the routine when both are known).
    openIngestion(feed) {
      this.$router.push(feed.routine_id
        ? dataIngestionLocation({ routineId: feed.routine_id })
        : dataIngestionLocation({ schemaId: feed.schema_id }));
    },
    isGroupOpen(domain) {
      return !!this.openGroups[domain];
    },
    toggleGroup(domain) {
      this.openGroups = { ...this.openGroups, [domain]: !this.openGroups[domain] };
    },
    countsSummary(group) {
      // e.g. "5 ok · 1 stale" — only non-zero, ok first
      const c = group.status_counts || {};
      const parts = [];
      if (c.on_schedule) parts.push(`${c.on_schedule} ok`);
      if (c.overdue) parts.push(`${c.overdue} overdue`);
      if (c.stale) parts.push(`${c.stale} stale`);
      if (c.never_succeeded) parts.push(`${c.never_succeeded} none`);
      return parts.join(' · ');
    },
    filesState(feed) {
      return this.filesByFeed[feed.feed_id] || {
        open: false, loading: false, error: null, files: [], loaded: false,
      };
    },
    async toggleFiles(feed) {
      const id = feed.feed_id;
      const current = this.filesState(feed);
      const next = { ...current, open: !current.open };
      this.filesByFeed = { ...this.filesByFeed, [id]: next };
      if (next.open && !next.loaded && !next.loading) {
        await this.loadFiles(feed);
      }
    },
    async loadFiles(feed) {
      const id = feed.feed_id;
      this.filesByFeed = {
        ...this.filesByFeed,
        [id]: { ...this.filesState(feed), open: true, loading: true, error: null },
      };
      try {
        const resp = await axios.get(`/api/etl_module/data-feeds/${id}/files/?limit=20`);
        this.filesByFeed = {
          ...this.filesByFeed,
          [id]: { open: true, loading: false, error: null, files: resp.data || [], loaded: true },
        };
      } catch (err) {
        this.filesByFeed = {
          ...this.filesByFeed,
          [id]: {
            ...this.filesState(feed),
            open: true,
            loading: false,
            error: err.response?.data?.detail || err.message || 'Failed to load files.',
          },
        };
      }
    },
    formatRows(n) {
      if (n == null) return '—';
      if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
      if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
      return String(n);
    },
    formatAgeHours(hours) {
      if (hours == null) return '';
      const days = hours / 24;
      if (days < 1) return `${Math.round(hours)}h`;
      if (days < 14) return `${Math.round(days)}d`;
      const weeks = days / 7;
      if (weeks < 9) return `${Math.round(weeks)}w`;
      return `${Math.round(days / 30)}mo`;
    },
  },
};
</script>
